import { useMemo, type ReactNode } from "react";

import { useAsyncQuery } from "@/ds";
import { getEmbarcacaoFicha } from "@/modules/embarcacoes/embarcacoes.gateway";
import type { EmbarcacaoFicha } from "@/modules/embarcacoes/embarcacoes.types";
import { EmbarcacaoFichaContext, type EmbarcacaoFichaCarga } from "./use-embarcacao-ficha";

const FICHA_FORBIDDEN_MESSAGE =
  "Você não tem permissão para consultar os detalhes desta embarcação.";
const FICHA_ERROR_MESSAGE = "Não foi possível carregar os detalhes da embarcação.";

function FichaCarregada({
  embarcacaoId,
  children,
}: {
  readonly embarcacaoId: string;
  readonly children: ReactNode;
}) {
  const { status, data, error, reload } = useAsyncQuery<EmbarcacaoFicha | null>({
    fetcher: (signal) => getEmbarcacaoFicha({ id: embarcacaoId, signal }),
    queryKey: embarcacaoId,
    initialData: null,
    forbiddenMessage: FICHA_FORBIDDEN_MESSAGE,
    errorMessage: FICHA_ERROR_MESSAGE,
  });

  const carga = useMemo<EmbarcacaoFichaCarga>(
    () => ({ embarcacaoId, status, ficha: data, error, reload }),
    [embarcacaoId, status, data, error, reload],
  );

  return (
    <EmbarcacaoFichaContext.Provider value={carga}>{children}</EmbarcacaoFichaContext.Provider>
  );
}

/**
 * Carrega a ficha completa (`GET /api/embarcacoes/{id}/all`) uma vez por abertura do detalhamento e a
 * compartilha com as 17 seções, que só selecionam o próprio bloco: nenhuma consulta parte de dentro delas.
 *
 * O `useAsyncQuery` aborta a carga em trânsito ao fechar, ao recarregar e ao trocar de embarcação, e descarta a
 * resposta abortada (em StrictMode, a primeira carga é abortada e só a segunda conta). A `key` remonta a carga na
 * troca de embarcação, para o estado da anterior nunca ser lido como o desta.
 */
export function EmbarcacaoFichaProvider({
  embarcacaoId,
  children,
}: {
  readonly embarcacaoId: string;
  readonly children: ReactNode;
}) {
  return (
    <FichaCarregada key={embarcacaoId} embarcacaoId={embarcacaoId}>
      {children}
    </FichaCarregada>
  );
}
